import { ERROR_SPECS, type ApiErrorBody, type ErrorCode } from '@natashu/shared';

/** Server error codes plus client-side conditions. */
export type ApiErrorCode = ErrorCode | 'NETWORK';

export const NETWORK_ERROR_MESSAGE = 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้';

export interface ApiErrorInit {
  code: ApiErrorCode;
  message: string;
  retryable: boolean;
  status: number;
  details?: unknown;
  correlationId?: string | null;
}

/** Every failed API call rejects with this. `message` is Thai text from the server. */
export class ApiError extends Error {
  override name = 'ApiError';
  readonly code: ApiErrorCode;
  readonly retryable: boolean;
  readonly status: number;
  readonly details: unknown;
  readonly correlationId: string | null;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.code = init.code;
    this.retryable = init.retryable;
    this.status = init.status;
    this.details = init.details;
    this.correlationId = init.correlationId ?? null;
  }
}

export function isApiError(err: unknown, code?: ApiErrorCode): err is ApiError {
  return err instanceof ApiError && (code === undefined || err.code === code);
}

/** Thai message for any thrown value (for toasts and inline errors). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return ERROR_SPECS.INTERNAL.th;
}

// ---------------------------------------------------------------------------
// Session hooks: the auth layer registers these so this module stays React-free.

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export interface ApiAuthHandlers {
  /** Session missing/expired (401 AUTH_REQUIRED). */
  onAuthRequired?: () => void;
  /** Password ok but the second factor has not been verified yet. */
  onMfaRequired?: () => void;
  /** Server demands a password change before other actions. */
  onPasswordChangeRequired?: () => void;
  /** Re-read /api/auth/me and return the fresh CSRF token. */
  refreshSession?: () => Promise<string | null>;
}

let authHandlers: ApiAuthHandlers = {};

export function configureApiAuth(handlers: ApiAuthHandlers): () => void {
  authHandlers = handlers;
  return () => {
    if (authHandlers === handlers) authHandlers = {};
  };
}

// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Plain objects are sent as JSON; FormData/Blob are sent as-is. */
  body?: unknown;
  query?: QueryParams;
  /** Sets `Idempotency-Key`. Create one per user intent with `newIdempotencyKey()` and reuse it on retry. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Do not trigger the global login/MFA redirects (auth screens handle these errors inline). */
  skipAuthRedirect?: boolean;
}

/**
 * `crypto.randomUUID` is a secure-context API: it is missing when the app is opened over plain
 * http (a phone on the same Wi-Fi during setup), which used to crash any page that made an id.
 * `crypto.getRandomValues` is available everywhere, so fall back to it.
 */
export function uuid(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < b.length; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6]! & 0x0f) | 0x40;
  b[8] = (b[8]! & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function newIdempotencyKey(): string {
  return uuid();
}

export function buildUrl(path: string, query?: QueryParams): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) if (v !== undefined && v !== null && v !== '') params.append(key, String(v));
  }
  const qs = params.toString();
  if (!qs) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
}

const SAFE_METHODS = new Set(['GET', 'HEAD']);

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return request<T>(path, options, false);
}

async function request<T>(path: string, options: RequestOptions, isRetry: boolean): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  // Custom header forces a CORS preflight for cross-site requests; required by /api/auth/login.
  headers.set('X-Requested-With', 'natashu');

  let body: BodyInit | undefined;
  if (options.body instanceof FormData || options.body instanceof Blob) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }
  if (!SAFE_METHODS.has(method) && csrfToken) headers.set('X-CSRF-Token', csrfToken);
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      body,
      credentials: 'include',
      signal: options.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError({ code: 'NETWORK', message: NETWORK_ERROR_MESSAGE, retryable: true, status: 0 });
  }

  if (response.ok) {
    if (response.status === 204) return undefined as T;
    const type = response.headers.get('content-type') ?? '';
    if (type.includes('application/json')) return (await response.json()) as T;
    return (await response.text()) as T;
  }

  const error = await toApiError(response);

  if (error.code === 'CSRF_INVALID' && !isRetry && !SAFE_METHODS.has(method) && authHandlers.refreshSession) {
    const fresh = await authHandlers.refreshSession().catch(() => null);
    if (fresh) return request<T>(path, options, true);
  }
  if (!options.skipAuthRedirect) {
    if (error.code === 'AUTH_REQUIRED') authHandlers.onAuthRequired?.();
    else if (error.code === 'AUTH_MFA_REQUIRED') authHandlers.onMfaRequired?.();
    else if (error.code === 'FORBIDDEN' && hasMustChangePassword(error.details)) authHandlers.onPasswordChangeRequired?.();
  }
  throw error;
}

function hasMustChangePassword(details: unknown): boolean {
  return typeof details === 'object' && details !== null && (details as { mustChangePassword?: unknown }).mustChangePassword === true;
}

async function toApiError(response: Response): Promise<ApiError> {
  const correlationId = response.headers.get('x-request-id');
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  const envelope = parsed as Partial<ApiErrorBody> | null;
  if (envelope?.error && typeof envelope.error.code === 'string') {
    return new ApiError({
      code: envelope.error.code,
      message: envelope.error.message || ERROR_SPECS[envelope.error.code]?.th || ERROR_SPECS.INTERNAL.th,
      retryable: Boolean(envelope.error.retryable),
      status: response.status,
      details: envelope.error.details,
      correlationId: envelope.error.correlationId ?? correlationId,
    });
  }
  // No JSON envelope: a proxy/gateway answered instead of the API.
  if (response.status >= 500 || response.status === 0) {
    return new ApiError({ code: 'NETWORK', message: NETWORK_ERROR_MESSAGE, retryable: true, status: response.status, correlationId });
  }
  if (response.status === 404) {
    return new ApiError({ code: 'NOT_FOUND', message: ERROR_SPECS.NOT_FOUND.th, retryable: false, status: 404, correlationId });
  }
  return new ApiError({ code: 'INTERNAL', message: ERROR_SPECS.INTERNAL.th, retryable: false, status: response.status, correlationId });
}

type BodylessOptions = Omit<RequestOptions, 'method' | 'body'>;

export const api = {
  get: <T>(path: string, options?: BodylessOptions) => apiFetch<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: BodylessOptions) => apiFetch<T>(path, { ...options, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, options?: BodylessOptions) => apiFetch<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, options?: BodylessOptions) => apiFetch<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: BodylessOptions & { body?: unknown }) => apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
